import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, label } from "@/lib/api";
import { MATTER_TYPES, opts } from "@/lib/constants";
import { PageHeader, Pill, Empty, GoldButton } from "@/components/bits";
import { FormDialog } from "@/components/FormDialog";
import { ConflictWarning } from "@/components/ConflictWarning";

export const clientFields = (lawyers) => [
  { name: "full_name", label: "Full legal name", required: true, col: 2 },
  { name: "client_type", label: "Client type", type: "select", required: true, options: [{ value: "individual", label: "Individual" }, { value: "corporate", label: "Corporate" }] },
  { name: "date_of_birth", label: "Date of birth", type: "date" },
  { name: "abn_acn", label: "ABN / ACN (corporate)", placeholder: "ACN 000 000 000" },
  { name: "matter_type", label: "Matter type", type: "select", required: true, options: opts(MATTER_TYPES) },
  { name: "email", label: "Email", type: "email" },
  { name: "phone", label: "Phone" },
  { name: "address", label: "Address", col: 2 },
  { name: "preferred_contact", label: "Preferred contact", type: "select", required: true, options: opts(["email", "phone", "sms", "post"]) },
  { name: "referral_source", label: "Referral source" },
  { name: "assigned_lawyer_id", label: "Assigned lawyer", type: "select", options: lawyers.map((l) => ({ value: l.id, label: l.name })) },
  { name: "notes", label: "Notes", type: "textarea", col: 2 },
];

export default function Clients() {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const load = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => { load(); api.get("/users").then((r) => setLawyers(r.data.filter((u) => u.role !== "support"))); }, []);

  const filtered = clients.filter((c) => `${c.full_name} ${c.email} ${c.phone}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="clients-page">
      <PageHeader eyebrow="CRM" title="Clients" subtitle={`${clients.length} client records`}
        actions={<GoldButton onClick={() => setOpen(true)} data-testid="new-client-button"><Plus className="w-4 h-4 mr-2" /> New client</GoldButton>} />
      <div className="relative mb-4 max-w-sm"><Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" /><input data-testid="clients-search-input" className="field pl-9" placeholder="Search clients…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      <div className="panel overflow-hidden fade-in">
        <table className="w-full">
          <thead><tr><th className="th">Client</th><th className="th">Type</th><th className="th">Matter type</th><th className="th">Contact</th><th className="th">KYC</th><th className="th">Matters</th><th className="th">Added</th></tr></thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="row-link" onClick={() => nav(`/clients/${c.id}`)} data-testid={`client-row-${c.id}`}>
                <td className="td font-medium text-white">{c.full_name}{c.abn_acn && <span className="block text-xs text-slate-500 font-mono">{c.abn_acn}</span>}</td>
                <td className="td capitalize text-slate-400">{c.client_type}</td>
                <td className="td text-slate-300">{label(c.matter_type)}</td>
                <td className="td text-slate-400 text-xs">{c.email}<br />{c.phone}</td>
                <td className="td"><Pill value={c.kyc_status} testId={`client-kyc-${c.id}`} /></td>
                <td className="td text-center">{c.matter_count}</td>
                <td className="td text-slate-400">{fmtDate(c.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <Empty text="No clients found." testId="clients-empty" />}
      </div>
      <FormDialog open={open} onOpenChange={setOpen} title="New client" description="Create a client record. KYC verification is required before a matter can be opened."
        fields={clientFields(lawyers)} initial={{ client_type: "individual", preferred_contact: "email", matter_type: "other" }} testPrefix="client" wide
        extra={(v) => <ConflictWarning name={v.full_name} email={v.email} phone={v.phone} />}
        onSubmit={async (v) => { const r = await api.post("/clients", v); toast.success("Client created — complete KYC to open matters"); nav(`/clients/${r.data.id}`); }} />
    </div>
  );
}
